import {Component, ViewEncapsulation, Input, Injector, OnInit, OnChanges, OnDestroy} from '@angular/core';
import {AbstractComponent} from '../abstract.component';
import { FiltersService } from 'projects/common/services/filters.service';
import { DiscreteFilter } from '../offers-filters/offers-filters-model';

export interface SuggestionData {
  price: string;
  price_formatted: string;
  departure_date: string;
  return_date: string;
  minimum_price: boolean;
  maximum_price: boolean;
}
export interface FareSuggestions {
  [key: string]: SuggestionData
}

@Component({
  selector: 'fare-suggestion-chart-container',
  template: `
    <text *ngIf="module.pretitle || module.title || module.subtitle" [data]="module" [uniqueId]="module.unique_id" [moduleId]="module.id" [moduleIndex]="module.row_number" class="-eva-3-mb-xlg"></text>

    <fare-suggestion-chart class="-eva-3-shadow-line -eva-3-mt-sm" [style.display]="'block'" [style.backgroundColor]="'var(--color-white)'"
                           *ngIf="showChart"
                           [fareSuggestionChartResponse]="module.data.service_response"
                           [locale]="module.data.locale"
                           [openNewTab]="true"
                           [chartType]="module.data.chart_type"
                           [onClickCallback]="onClickSuggestion"
                           product="flights"
                           flow="landing" ></fare-suggestion-chart>
  `,
  styleUrls: ['./fare-suggestion-chart-container.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FareSuggestionChartContainerComponent extends AbstractComponent implements OnInit, OnChanges, OnDestroy {
  @Input() override module: any;
  
  showChart = true;
  currentCurrencyCode;

  private subscriptions: any[] = [];

  constructor(injector: Injector, protected filtersService: FiltersService) {
    super(injector);
  }

  ngOnInit(): void {
    const suggestions = this.module.data.service_response.fare_suggestions;
    const withCallbacksEnabled = Object.values(suggestions).some((s: any) => !s.link);

    if (withCallbacksEnabled) {
      if (this.module.data.locale === 'es_AR') {
        this.replaceArMinPrice();
      }
      this.subscriptions.push(
        this.filtersService.filtersAppliedCountEvent
          .subscribe(({count, filters}) => {
            this.setHighlightedValues();
          })
      );
    }
    this.currentCurrencyCode = this.module.data.service_response.currency.code;
  }

  setHighlightedValues() {
    const suggestions = this.module.data.service_response.fare_suggestions;
    const filters = this.filtersService.filters;

    Object.entries(suggestions).forEach(([key, value]: [string, any]) => {
      const filterKey = this.getMonthKey(key);
      let highlight;
      if (filters[filterKey]) {
        const filter = filters[filterKey] as DiscreteFilter;
        const allItemsChoiceSelected = filter.values.find(v => v.all_items_choice)?.selected;
        highlight = allItemsChoiceSelected || filter.values.filter(v => !v.all_items_choice).every(v => v.selected);
      }
      value.highlight = highlight;
    });
    this.module.data.service_response = { ...this.module.data.service_response };
  }

  ngOnChanges(): void {
    this.setHighlightedValues();    
    if (this.module.data.locale === 'es_AR' && this.currentCurrencyCode == this.module.data.service_response.currency.code) {
      this.replaceArMinPrice();
    }
    this.currentCurrencyCode = this.module.data.service_response.currency.code;
  }

  replaceArMinPrice() {
    const suggestions: FareSuggestions = this.module.data.service_response.fare_suggestions;
    const oldMinimumPriceSuggestion: SuggestionData | undefined = Object.values(suggestions).find(s => s.minimum_price);
    
    const cheapestMonthPriceKeywords = this.module.data.cheapest_month_price_keywords;
    if (oldMinimumPriceSuggestion && cheapestMonthPriceKeywords && cheapestMonthPriceKeywords.priceAmount && cheapestMonthPriceKeywords.formattedPrice) {
      
      this.module.data.service_response.min_fare_suggestion = {
        "price": parseInt(cheapestMonthPriceKeywords.priceAmount),
        "price_formatted": cheapestMonthPriceKeywords.formattedPrice?.split(' ')[1] || cheapestMonthPriceKeywords.formattedPrice,
        "minimum_price": true,
        "maximum_price": false
      };
      if (cheapestMonthPriceKeywords.cheapestMonth) {

        const entry = Object.entries(suggestions).find(([monthKey, suggestion]) => this.getCheapestMonthFormatKey(suggestion.departure_date) === cheapestMonthPriceKeywords.cheapestMonth);
        if (entry) {
          const [monthKey, newMinSuggestion] = entry;
          if (newMinSuggestion) {
            suggestions[monthKey] = { ...suggestions[monthKey], ...this.module.data.service_response.min_fare_suggestion };
            oldMinimumPriceSuggestion.minimum_price = false;
          }
        }
      }
    }
    
  }

  getModuleId(): string {
    return 'FareSuggestionChartModule';
  }

  onClickSuggestion = (suggestion: any) => {
    if (!suggestion) return;
    const key = this.getMonthKey(suggestion.departure_date);
    const filter = this.filtersService.filters[key] as DiscreteFilter;
    if (!filter) {
      console.log("Filter not found");
      //TODO si se da este caso se podría crear dinámicamente un nuevo filtro con única opción de todo el mes
      return;
    }
    const allItemsChoiceSelected = filter.values.find(v => v.all_items_choice)?.selected;
    const selectedValue = filter.values.filter(v => !v.all_items_choice).some(v => !v.selected);
    filter.values.forEach(value => {
      value.selected = !allItemsChoiceSelected && selectedValue
    });
    filter.hidden = false;
    this.filtersService.updateFiltersState(this.filtersService.filters, this.module.row_number, this.module.unique_id);
    this.filtersService.getSelectedFilterLabels();
    this.filtersService.applyFilter();
  }

  getMonthKey(dateStr: string): string {
    const sinceDate = new Date(dateStr);
    const locale = this.module.data.locale.replace("_", "-");
    const month = sinceDate.toLocaleString(locale, { timeZone: 'UTC', month: 'long' });
    const year = sinceDate.toLocaleString(locale, { timeZone: 'UTC', year: 'numeric' });
    return 'weeksRange_' + month.charAt(0).toUpperCase() + month.slice(1) + ` ${year}`;
  }

  getCheapestMonthFormatKey(dateStr: string): string {
    const monthYear = new Date(dateStr);
    const locale = this.module.data.locale.replace("_", "-");
    const month = monthYear.toLocaleString(locale, { timeZone: 'UTC', month: 'long' });
    const year = monthYear.toLocaleString(locale, { timeZone: 'UTC', year: 'numeric' });
    return month + ` ${year}`;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => {
      s.unsubscribe();
    });
  }
}
